import React, { useState, useEffect } from "react";
import {
  BackArrowButton,
  CancelButton,
  ContinueButton,
  CloseButton,
} from "../../../elements/button/Button";
import CustomDropdown from "../CustomDropdown/CustomDropdown";
import styles from "./ColumnMapping.module.css";
import commonstyles from "../Forms.module.css";
import ErrorPopup from "../../../elements/ErrorPopup";
import {
  getPurchaseMapping,
  updatePurchaseMapping,
} from "../../../../Bookclosure/Services/ApiHandler";

const LABEL_FIELDS = [
  "Document Type",
  "Invoice Number",
  "Vendor Code",
  "Vendor Name",
  "Pay To Vendor Code",
  "Pay To Vendor Name",
  "Ship To Vendor Code",
  "Ship To Vendor Name",
  "Currency",
  "Invoice Date",
  "Payment Terms",
  "Due Date",
  "Amount",
  "Total Amount",
  "GST",
  "Discounts & Others",
];

const KEY_FIELDS = [
  "documentType",
  "invoiceNumber",	 
  "vendorCode",	 
  "vendorName",	 
  "payToVendorCode",
  "payToVendorName",
  "shipToVendorCode",
  "shipToVendorName",
  "currency",
  "invoiceDate",	 
  "paymentTerms",
  "dueDate",
  "amount",
  "totalAmount", 
  "GST",  
  "discountsAndOthers",
];

const LABEL_TO_KEY = LABEL_FIELDS.reduce((acc, label, index) => {
  acc[label] = KEY_FIELDS[index];
  return acc;
}, {});

const DEFAULT_MAPPING = {
  documentType: "Document Type",
  invoiceNumber: "Invoice Number",
  vendorCode: "Vendor Code",	 
  vendorName: "Vendor Name",	 
  payToVendorCode: "Pay To Vendor Code",
  payToVendorName: "Pay To Vendor Name",
  shipToVendorCode: "Ship To Vendor Code",
  shipToVendorName:  "Ship To Vendor Name",
  currency: "Currency",	 
  invoiceDate: "Invoice Date",	 
  paymentTerms: "Payment Terms",
  dueDate: "Due Date",
  amount: "Amount",	 
  totalAmount: "Total Amount",	 
  GST: "GST",  
  discountsAndOthers: "Discounts & Others",
};

const ColumnMapping = ({
  PurchaseID,
  onContinue,
  onBack,
  onClose,
}) => {
  const [columnOptions, setColumnOptions] = useState([]);

  const [mappedValues, setMappedValues] = useState(
    KEY_FIELDS.reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [errorPopup, setErrorPopup] = useState({
    isOpen: false,
    title: "Error",
    message: "",
  });

  useEffect(() => {
    const fetchMapping = async () => {
      setLoading(true);
      setApiError(null);

      try {
        const response = await getPurchaseMapping(PurchaseID);
        const fields = response?.data?.fields || [];

        setColumnOptions(fields);

        const autoMapped = {};
        KEY_FIELDS.forEach((key) => {
          const defaultValue = DEFAULT_MAPPING[key];
          if (fields.includes(defaultValue)) {
            autoMapped[key] = defaultValue;
          }
        });

        setMappedValues((prev) => ({ ...prev, ...autoMapped }));
      } catch (error) {
        console.error("Error fetching Purchase mapping:", error);
        setApiError("Failed to load");
      }

      setLoading(false);
    };

    fetchMapping();
  }, [PurchaseID]);

  const handleFieldChange = (label, value) => {
    const key = LABEL_TO_KEY[label];
    setMappedValues((prev) => ({ ...prev, [key]: value }));
  };

  const fieldsMapped = KEY_FIELDS.every(
    (key) => mappedValues[key] && mappedValues[key] !== ""
  );

  const handleSubmit = async () => {
    if (!fieldsMapped) {
      setErrorPopup({
        isOpen: true,
        title: "Required Fields Missing",
        message: "Please fill all required fields.",
      });
      return;
    }

    try {
      setLoading(true);

      await updatePurchaseMapping(PurchaseID, mappedValues);

      onContinue();
    } catch (error) {
      console.error("Error updating Purchase mapping:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to update mapping.";

      setErrorPopup({
        isOpen: true,
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={commonstyles.backdrop}>
        <div
          className={`${commonstyles.container} ${commonstyles.largeContainer}`}
        >
          <header className={`${commonstyles.header}`}>
            <div className={commonstyles.headerLeft}>
              <BackArrowButton onClick={onBack} />
              <h2 className={commonstyles.heading}>Map Column Headers</h2>
            </div>
            <CloseButton onClick={onClose} />
          </header>

          <form className={styles.gridForm}>
            {LABEL_FIELDS.map((label) => (
              <CustomDropdown
                key={label}
                label={label}
                required
                value={mappedValues[LABEL_TO_KEY[label]] || ""}
                onChange={(value) => handleFieldChange(label, value)}
                options={columnOptions}
                placeholder="Select Column"
              />
            ))}
          </form>

          <div className={commonstyles.footer}>
            <CancelButton onClick={onClose} />

            <ContinueButton
              onClick={handleSubmit}
              disabled={!fieldsMapped || loading || apiError}
            />
          </div>
        </div>
      </div>

      <ErrorPopup
        isOpen={errorPopup.isOpen}
        title={errorPopup.title}
        message={errorPopup.message}
        onClose={() =>
          setErrorPopup({ isOpen: false, title: "Error", message: "" })
        }
      />
    </>
  );
};

export default ColumnMapping;
